import React from 'react';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useQueryClient } from '@tanstack/react-query';
import { useICSSubscriptionEventsQuery } from '@uplift/queries';
import { useCurrentuser, useTimezone } from '@uplift/contexts';
import { Appointment, ignoreICSConflict } from '@uplift/api';
import { AsyncButton, Button } from '../../../../../Button';
import { Alert } from '../../../../../Alert';
import { useAlert } from '../../../../../AlertProvider';
import { formatDateTimeRange } from '@uplift/services';


const useStyles = makeStyles()(theme => ({
  warningAlert: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
}));


export type AppointmentConflictAlertProps = {
  appointment?: Appointment;
  onCancelSession: (appointment: Appointment) => void;
};


export const AppointmentConflictAlert: React.VFC<AppointmentConflictAlertProps> = ({ appointment, onCancelSession }) => {
  const me = useCurrentuser();
  const timezone = useTimezone();
  const queryClient = useQueryClient();
  const { classes } = useStyles();
  const { data: icsData } = useICSSubscriptionEventsQuery(me.id);
  const alert = useAlert();
  const conflictEvent = React.useMemo(() => {
    if (!appointment?.id) {
      return null;
    }

    const currentAppointmentConflict = icsData?.conflicts.find(c => c.appointment.id === appointment.id);

    if (currentAppointmentConflict) {
      const icsEvent = icsData?.events.find(e => e.id === currentAppointmentConflict.icsEventId);

      return icsEvent;
    }

    return null;
  }, [ icsData, appointment?.id ]);

  if (!conflictEvent) {
    return null;
  }

  return (
    <Alert flat severity="warning" title="This session conflicts with another event" className={classes.warningAlert}>
      <span style={{ fontWeight: 450 }}>{conflictEvent.title}</span> on <span style={{ fontWeight: 450 }}>{formatDateTimeRange(conflictEvent.fromDatetime, conflictEvent.toDatetime, timezone)}</span> from <span style={{ fontWeight: 450 }}>Google Calendar</span>

      <Box display="flex" gap="8px" mt={2}>
        <Button
          color="error" variant="contained" size="small"
          onClick={() => {
            if (!appointment) return;

            onCancelSession(appointment);
          }}
        >cancel session</Button>
        <AsyncButton
          size="small"
          onClick={async () => {
            if (!appointment) {
              return;
            }

            await ignoreICSConflict(conflictEvent.id, appointment.id);
            await queryClient.invalidateQueries([ 'icsSubscription' ]);
            alert.success('Conflict ignored');
          }}
        >ignore conflict</AsyncButton>
      </Box>
    </Alert>
  );
};
